// FILE: CollegeUploadLogs.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, RefreshCw, Download, CheckCircle, XCircle,
  AlertTriangle, Clock, ChevronDown, ChevronUp
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

const downloadCSVFromString = (csvString, filename) => {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const STATUS_CONFIG = {
  COMPLETED:  { color: 'bg-green-100 text-green-800',  icon: <CheckCircle  size={14} className="text-green-600" />,   label: 'Completed'  },
  PARTIAL:    { color: 'bg-yellow-100 text-yellow-800', icon: <AlertTriangle size={14} className="text-yellow-600" />, label: 'Partial'    },
  FAILED:     { color: 'bg-red-100 text-red-800',       icon: <XCircle      size={14} className="text-red-600" />,     label: 'Failed'     },
  PROCESSING: { color: 'bg-blue-100 text-blue-800',     icon: <Clock        size={14} className="text-blue-600" />,   label: 'Processing' },
};

// Inline style equivalents for status badges
const STATUS_BADGE_STYLES = {
  COMPLETED:  { background: '#DCFCE7', color: '#16A34A', border: '1px solid #BBF7D0' },
  PARTIAL:    { background: '#FEF9C3', color: '#CA8A04', border: '1px solid #FDE047' },
  FAILED:     { background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' },
  PROCESSING: { background: '#EFF6FF', color: 'var(--primary-blue, #4F46E5)', border: '1px solid #BFDBFE' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const CollegeUploadLogs = ({ collegeId }) => {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [errorRows, setErrorRows]   = useState({}); // logId -> rows[]
  const [loadingErrors, setLoadingErrors] = useState({});

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/students/upload-logs`, { college_id: collegeId });
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch upload logs:', err);
      setError('Failed to load upload history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [collegeId]);

  useEffect(() => {
    if (collegeId) fetchLogs();
  }, [collegeId, fetchLogs]);

  // Expand/collapse a log row and lazy-load its errors
  const toggleExpand = async (logId, hasFailed) => {
    if (expandedId === logId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(logId);

    if (hasFailed && !errorRows[logId]) {
      setLoadingErrors(prev => ({ ...prev, [logId]: true }));
      try {
        const res = await axios.get(`${API_BASE_URL}/students/error-report/${logId}`);
        if (res.data?.csv_data) {
          // Parse the CSV string into rows for display
          const lines = res.data.csv_data.split('\n').filter(Boolean);
          const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, ''));
          const rows = lines.slice(1).map(line => {
            const cells = line.split(',').map(c => c.replace(/^"|"$/g, ''));
            return Object.fromEntries(headers.map((h, i) => [h, cells[i] ?? '']));
          });
          setErrorRows(prev => ({ ...prev, [logId]: rows }));
        }
      } catch (err) {
        console.error('Failed to load errors for log', logId, err);
      } finally {
        setLoadingErrors(prev => ({ ...prev, [logId]: false }));
      }
    }
  };

  const handleDownloadErrors = async (logId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/students/error-report/${logId}`);
      if (res.data?.csv_data) {
        downloadCSVFromString(res.data.csv_data, `error_report_upload_${logId}.csv`);
      }
    } catch (err) {
      console.error('Failed to download error report:', err);
      alert('Could not download error report. Please try again.');
    }
  };

  // -- Loading ---------------------------------------------------------------

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 16px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            border: '3px solid #BFDBFE', borderTopColor: 'var(--primary-blue, #4F46E5)',
            animation: 'spin 0.7s linear infinite',
            margin: '0 auto 14px',
          }} />
          <p style={{ color: '#64748B', fontSize: '0.875rem', margin: 0 }}>Loading upload history...</p>
        </div>
      </div>
    );
  }

  // -- Error -----------------------------------------------------------------

  if (error) {
    return (
      <div style={{
        padding: '18px 20px',
        background: '#FEF2F2',
        border: '1px solid #FECACA',
        borderRadius: '12px',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <XCircle style={{ color: '#EF4444', flexShrink: 0 }} size={22} />
        <div style={{ flex: 1 }}>
          <p style={{ fontWeight: 700, color: '#991B1B', margin: '0 0 2px', fontSize: '0.9rem' }}>Error Loading Logs</p>
          <p style={{ fontSize: '0.85rem', color: '#B91C1C', margin: 0 }}>{error}</p>
        </div>
        <button
          onClick={fetchLogs}
          style={{
            padding: '7px 16px',
            background: '#DC2626', color: '#FFFFFF',
            borderRadius: '8px', border: 'none', cursor: 'pointer',
            fontSize: '0.8rem', fontWeight: 700,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // -- Empty -----------------------------------------------------------------

  if (logs.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '72px 16px' }}>
        <div style={{
          width: 72, height: 72, borderRadius: '18px',
          background: '#F1F5F9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <FileText size={34} style={{ color: '#CBD5E1' }} />
        </div>
        <p style={{ color: '#374151', fontWeight: 700, fontSize: '1rem', margin: '0 0 6px' }}>No Upload History</p>
        <p style={{ color: '#94A3B8', fontSize: '0.875rem', margin: '0 0 20px' }}>
          Bulk uploads you perform will appear here.
        </p>
        <button
          onClick={fetchLogs}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 18px',
            border: '1px solid var(--border-subtle, #E2E8F0)',
            borderRadius: '9px', background: '#FFFFFF', cursor: 'pointer',
            fontSize: '0.85rem', fontWeight: 600, color: '#374151',
            transition: 'background 0.15s',
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
    );
  }

  // -- Summary stats ---------------------------------------------------------

  const totalUploaded = logs.reduce((s, l) => s + (l.total_rows || 0), 0);
  const totalSuccess  = logs.reduce((s, l) => s + (l.successful_rows || 0), 0);
  const totalFailed   = logs.reduce((s, l) => s + (l.failed_rows || 0), 0);

  // -- Render ----------------------------------------------------------------

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        {/* Total Uploads */}
        <div style={{ background: '#FFFFFF', borderRadius: '14px', padding: '20px', border: '1px solid var(--border-subtle, #E2E8F0)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'var(--primary-blue, #4F46E5)', borderRadius: '14px 14px 0 0' }} />
          <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 8px' }}>Total Uploads</p>
          <p style={{ fontSize: '1.875rem', fontWeight: 800, color: '#0F172A', margin: 0, lineHeight: 1 }}>{logs.length}</p>
        </div>
        {/* Success */}
        <div style={{ background: '#FFFFFF', borderRadius: '14px', padding: '20px', border: '1px solid var(--border-subtle, #E2E8F0)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#16A34A', borderRadius: '14px 14px 0 0' }} />
          <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 8px' }}>Students Successfully Added</p>
          <p style={{ fontSize: '1.875rem', fontWeight: 800, color: '#16A34A', margin: 0, lineHeight: 1 }}>{totalSuccess}</p>
        </div>
        {/* Failed */}
        <div style={{ background: '#FFFFFF', borderRadius: '14px', padding: '20px', border: '1px solid var(--border-subtle, #E2E8F0)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#DC2626', borderRadius: '14px 14px 0 0' }} />
          <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 8px' }}>Total Rows Rejected</p>
          <p style={{ fontSize: '1.875rem', fontWeight: 800, color: '#DC2626', margin: 0, lineHeight: 1 }}>{totalFailed}</p>
        </div>
      </div>

      {/* Header + refresh */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.95rem', margin: 0 }}>
          Upload History <span style={{ color: '#94A3B8', fontWeight: 500 }}>({logs.length} sessions)</span>
        </h3>
        <button
          onClick={fetchLogs}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '7px 14px',
            border: '1px solid var(--border-subtle, #E2E8F0)',
            borderRadius: '9px', background: '#FFFFFF', cursor: 'pointer',
            fontSize: '0.8rem', fontWeight: 600, color: '#374151',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
          onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Logs table */}
      <div style={{ background: '#FFFFFF', borderRadius: '14px', border: '1px solid var(--border-subtle, #E2E8F0)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead style={{ background: '#F8FAFC' }}>
            <tr>
              {['Date & Time', 'File', 'Uploaded By', 'Total', 'Success', 'Failed', 'Credits', 'Status', 'Actions'].map((h, i) => (
                <th key={h} style={{
                  padding: '12px 14px',
                  textAlign: i >= 3 && i <= 8 ? 'center' : 'left',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  borderBottom: '1px solid var(--border-subtle, #E2E8F0)',
                  whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => {
              const cfg       = STATUS_CONFIG[log.status] || STATUS_CONFIG.PROCESSING;
              const badgeStyle = STATUS_BADGE_STYLES[log.status] || STATUS_BADGE_STYLES.PROCESSING;
              const isExpanded = expandedId === log.id;
              const hasFailed  = (log.failed_rows || 0) > 0;

              return (
                <React.Fragment key={log.id}>
                  <tr style={{
                    background: isExpanded ? '#F8FAFC' : '#FFFFFF',
                    borderBottom: '1px solid var(--border-subtle, #E2E8F0)',
                    transition: 'background 0.1s',
                  }}
                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isExpanded ? '#F8FAFC' : '#FFFFFF'; }}
                  >
                    {/* Date */}
                    <td style={{ padding: '13px 14px', color: '#374151', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                      {formatDate(log.uploaded_at)}
                    </td>

                    {/* File */}
                    <td style={{ padding: '13px 14px', color: '#374151', maxWidth: 180 }} title={log.file_name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FileText size={14} style={{ color: '#94A3B8', flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.file_name || 'bulk_upload.csv'}
                        </span>
                      </div>
                    </td>

                    {/* Uploaded By */}
                    <td style={{ padding: '13px 14px', color: '#64748B', fontSize: '0.8rem' }}>
                      {log.uploaded_by || '-'}
                    </td>

                    {/* Total */}
                    <td style={{ padding: '13px 14px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>
                      {log.total_rows ?? 0}
                    </td>

                    {/* Success */}
                    <td style={{ padding: '13px 14px', textAlign: 'center', fontWeight: 800, color: '#16A34A' }}>
                      {log.successful_rows ?? 0}
                    </td>

                    {/* Failed */}
                    <td style={{ padding: '13px 14px', textAlign: 'center', fontWeight: 800, color: '#DC2626' }}>
                      {log.failed_rows ?? 0}
                    </td>

                    {/* Credits */}
                    <td style={{ padding: '13px 14px', textAlign: 'center', color: '#64748B' }}>
                      {log.credits_deducted ?? 0}
                    </td>

                    {/* Status badge */}
                    <td style={{ padding: '13px 14px', textAlign: 'center' }}>
                      <span style={{
                        ...badgeStyle,
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '0.72rem', fontWeight: 700,
                      }}>
                        {cfg.icon}
                        {cfg.label}
                      </span>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '13px 14px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        {hasFailed && (
                          <button
                            onClick={() => handleDownloadErrors(log.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              padding: '5px 10px',
                              background: '#FEF2F2', color: '#DC2626',
                              border: '1px solid #FECACA',
                              borderRadius: '8px', cursor: 'pointer',
                              fontSize: '0.72rem', fontWeight: 700,
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#FEE2E2'}
                            onMouseLeave={e => e.currentTarget.style.background = '#FEF2F2'}
                            title="Download error report"
                          >
                            <Download size={11} />
                            Errors
                          </button>
                        )}
                        {hasFailed && (
                          <button
                            onClick={() => toggleExpand(log.id, hasFailed)}
                            style={{
                              padding: '5px', borderRadius: '8px',
                              border: '1px solid var(--border-subtle, #E2E8F0)',
                              background: '#FFFFFF', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F1F5F9'}
                            onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
                            title={isExpanded ? 'Collapse' : 'View failed rows'}
                          >
                            {isExpanded
                              ? <ChevronUp size={15} style={{ color: '#374151' }} />
                              : <ChevronDown size={15} style={{ color: '#374151' }} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded error rows */}
                  {isExpanded && hasFailed && (
                    <tr>
                      <td colSpan={9} style={{
                        background: '#FFF8F8',
                        padding: '16px 20px',
                        borderBottom: '1px solid #FECACA',
                        borderTop: '1px solid #FECACA',
                      }}>
                        {loadingErrors[log.id] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748B', fontSize: '0.875rem' }}>
                            <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #BFDBFE', borderTopColor: 'var(--primary-blue, #4F46E5)', animation: 'spin 0.7s linear infinite' }} />
                            Loading error details...
                          </div>
                        ) : errorRows[log.id] && errorRows[log.id].length > 0 ? (
                          <div>
                            <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#DC2626', margin: '0 0 10px' }}>
                              Failed Rows ({errorRows[log.id].length})
                            </p>
                            <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #FECACA' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                                <thead style={{ background: '#FEE2E2' }}>
                                  <tr>
                                    {Object.keys(errorRows[log.id][0] || {}).map(h => (
                                      <th key={h} style={{
                                        padding: '9px 12px', textAlign: 'left',
                                        fontWeight: 700, color: '#991B1B', whiteSpace: 'nowrap',
                                        borderBottom: '1px solid #FECACA',
                                      }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody style={{ background: '#FFFFFF' }}>
                                  {errorRows[log.id].map((row, ri) => (
                                    <tr key={ri} style={{ borderTop: '1px solid #FEE2E2' }}>
                                      {Object.values(row).map((v, ci) => (
                                        <td key={ci} style={{ padding: '8px 12px', color: '#374151' }}>{v || '-'}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.875rem', color: '#94A3B8', margin: 0 }}>
                            No detailed error data available for this upload.
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CollegeUploadLogs;

